import { getLogger } from "@expert/logging";
import { Autocomplete, Box, Group, type RenderAutocompleteOption, Text } from "@mantine/core";
import { type CountryCode, getCountryCallingCode, isSupportedCountry } from "libphonenumber-js";
import { useState } from "react";
import { type CountryIso2, FlagImage } from "react-international-phone";
import styles from "./CountrySelect.module.css";
import { countryNameFilter, getCountryItems, getRegionName } from "./utils";

const logger = getLogger({ module: "CountrySelect" });

/**
 * libphonenumber and react-international-phone have different requirements for country code.
 * We store our data to libphonenumber's uppercase standard e.g "US"
 * so we must cast toLowerCase() to interface with react-international-phone
 *
 * @see https://react-international-phone.vercel.app/docs/Subcomponents-API/CountrySelector
 * @see https://www.npmjs.com/package/libphonenumber-js
 */
interface CountrySelectProps {
    clientEnabledCountries?: CountryCode[];
    setCountry: (country: CountryIso2) => void;
    disabled?: boolean;
    defaultCountry?: CountryCode;
}

const FlagIcon = ({ code }: { code: CountryCode }) => (
    <Box w="xl" className={styles.flagContainer}>
        <FlagImage iso2={code.toLowerCase()} />
    </Box>
);

export const CountryOption = ({ optionLabel, value }: { optionLabel: string; value: CountryCode }) => {
    return (
        <Group w="100%" gap="md">
            <FlagIcon code={value} />
            <Text span>{optionLabel}</Text>
        </Group>
    );
};

const renderOption: RenderAutocompleteOption = ({ option }): React.JSX.Element => {
    const countryCode = option.value as CountryCode;
    const regionName = getRegionName(countryCode);
    const dialCode = getCountryCallingCode(countryCode);
    const optionLabel = `${regionName} +${dialCode}`;

    return <CountryOption optionLabel={optionLabel} value={countryCode} />;
};

export function CountrySelect({
    clientEnabledCountries,
    setCountry,
    defaultCountry,
    disabled = false,
}: CountrySelectProps) {
    const countryItems = getCountryItems(clientEnabledCountries);
    const [code, setCode] = useState<CountryCode | undefined>(defaultCountry);

    const onOptionSubmit = (option: string) => {
        const selected = countryItems.find((c) => c.value === option);
        if (selected && isSupportedCountry(selected.value)) {
            setCode(selected.value);
            setCountry(selected.value.toLowerCase()); // react-international-phone expects lowercase country codes
        }
    };

    const handleChange = (value: string) => {
        if (isSupportedCountry(value)) {
            setCode(value);
        } else {
            logger.error(`Error - invalid country code input. Expecting format "US".`);
        }
    };

    return (
        <Autocomplete
            value={code}
            className={styles.autocomplete}
            data={countryItems}
            onChange={handleChange}
            onOptionSubmit={onOptionSubmit}
            disabled={disabled}
            leftSection={code && <FlagIcon code={code} />}
            renderOption={renderOption}
            comboboxProps={{
                position: "bottom-start",
            }}
            styles={{
                dropdown: {
                    minWidth: "fit-content",
                },
            }}
            filter={countryNameFilter}
            onClear={() => setCode(undefined)}
            clearable
        />
    );
}
